/**
 * The playhead broadcast.
 *
 * The playhead moves 60 times a second. Putting it in React state would
 * re-render the whole editor 60 times a second, which is exactly the mistake
 * that makes browser DAWs feel sluggish. Instead the position lives here, is
 * read straight from the audio clock, and is pushed to a small number of
 * subscribers that update DOM transforms or canvas draws directly.
 *
 * The loop stops itself when nothing is subscribed and when the transport is
 * idle, so an idle project costs no frames.
 */

export type PlayheadListener = (ticks: number, seconds: number) => void;

export class PlayheadClock {
  private listeners = new Set<PlayheadListener>();
  private frame: number | null = null;
  private running = false;

  constructor(
    private readonly readTicks: () => number,
    private readonly readSeconds: () => number,
  ) {}

  subscribe(listener: PlayheadListener): () => void {
    this.listeners.add(listener);
    this.ensureRunning();
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Called by the transport so the loop can idle when nothing is moving. */
  setRunning(running: boolean): void {
    this.running = running;
    this.ensureRunning();
    if (!running) this.emit();
  }

  /** Pushes the current position immediately, e.g. after a seek while stopped. */
  emit(): void {
    const ticks = this.readTicks();
    const seconds = this.readSeconds();
    for (const listener of this.listeners) listener(ticks, seconds);
  }

  private ensureRunning(): void {
    const shouldRun = this.running && this.listeners.size > 0;
    if (shouldRun && this.frame === null) {
      const step = (): void => {
        this.emit();
        this.frame = this.running && this.listeners.size > 0 ? requestAnimationFrame(step) : null;
      };
      this.frame = requestAnimationFrame(step);
    } else if (!shouldRun && this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  dispose(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.listeners.clear();
  }
}
